// AmrieBreakpointBrowser.js — read-only filterable table of all AMRIE
// breakpoints. Org-agnostic: shows the same data for every user,
// independent of which organization is selected.
//
// Purpose: lets a lab admin survey what AMRIE already covers BEFORE
// defining a custom breakpoint, so they can avoid duplicating existing
// rules and target gaps deliberately.
//
// Rendered as an Accordion at the top of CustomLists.js, collapsed by
// default. Reads the breakpoints + antibiotics arrays via props (loaded
// once by the parent) — no separate fetches.
//
// Filters live in column-header dropdowns and combine with AND. Each
// dropdown lists distinct values from the WHOLE dataset (not the
// currently filtered subset), so changing one filter never invalidates
// what's selected in another. Free-text input on the Organism column
// covers the ~2,350-entry case where a dropdown would be unwieldy.
//
// Pagination: MUI TablePagination at the bottom. Filters apply to the
// whole dataset, then pagination slices the filtered result for display.

import React, { useMemo, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

const ALL = "(all)";

// Distinct sorted values from a list of objects, picking field `key`.
// Empty/null values are mapped to "(General)" for the site column and
// otherwise skipped. Returns an array beginning with the ALL sentinel.
function distinctValues(items, key, opts = {}) {
  const set = new Set();
  for (const it of items) {
    const v = it?.[key];
    if (v == null || (typeof v === "string" && !v.trim())) {
      if (opts.blankLabel) set.add(opts.blankLabel);
      continue;
    }
    set.add(v);
  }
  return [ALL, ...Array.from(set).sort()];
}

export default function AmrieBreakpointBrowser({
  breakpoints,
  antibiotics,
}) {
  // Column filter state — all default to "(all)" (no filtering).
  const [filterAntibiotic, setFilterAntibiotic] = useState(ALL);
  const [filterPotency, setFilterPotency] = useState(ALL);
  const [filterOrganism, setFilterOrganism] = useState(""); // free-text
  const [filterHost, setFilterHost] = useState(ALL);
  const [filterSite, setFilterSite] = useState(ALL);
  const [filterGuideline, setFilterGuideline] = useState(ALL);

  // Pagination state.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Lookup map for resolving antibiotic display names.
  const antibioticByCode = useMemo(() => {
    const map = new Map();
    for (const a of antibiotics || []) {
      if (a?.whonetAbxCode) map.set(a.whonetAbxCode, a);
    }
    return map;
  }, [antibiotics]);

  // Compose a single guideline display string from `guidelines + year`.
  // Example: "EUCAST 2026" or "CLSI 2026".
  const composeGuideline = (bp) => {
    const g = (bp?.guidelines || "").trim();
    const y = (bp?.year || "").toString().trim();
    if (g && y) return `${g} ${y}`;
    return g || y || "-";
  };

  // Distinct-value helpers for the dropdowns. Computed from the WHOLE
  // breakpoints dataset (not the filtered one) — simpler UX, no
  // dropdown contents shifting under the user as they refine.
  const antibioticOptions = useMemo(() => {
    const set = new Set();
    for (const bp of breakpoints || []) {
      if (bp?.whonetAbxCode) set.add(bp.whonetAbxCode);
    }
    // Sort by display name (antibiotic full name) when available,
    // otherwise by the code itself.
    const sorted = Array.from(set).sort((a, b) => {
      const an = antibioticByCode.get(a)?.antibiotic || a;
      const bn = antibioticByCode.get(b)?.antibiotic || b;
      return an.localeCompare(bn);
    });
    return [ALL, ...sorted];
  }, [breakpoints, antibioticByCode]);

  const potencyOptions = useMemo(
    () => distinctValues(breakpoints || [], "potency"),
    [breakpoints],
  );
  const hostOptions = useMemo(
    () => distinctValues(breakpoints || [], "host"),
    [breakpoints],
  );
  const siteOptions = useMemo(
    () => distinctValues(breakpoints || [], "siteOfInfection", {
      blankLabel: "(General)",
    }),
    [breakpoints],
  );
  const guidelineOptions = useMemo(() => {
    const set = new Set();
    for (const bp of breakpoints || []) {
      const g = composeGuideline(bp);
      if (g && g !== "-") set.add(g);
    }
    return [ALL, ...Array.from(set).sort()];
  }, [breakpoints]);

  // Apply all filters (AND-combined) to produce the filtered row set.
  // The Organism filter is free-text and matches case-insensitively
  // against either the organism code or its type (e.g. typing "EBC"
  // narrows to family-level rules; typing "ALL" narrows to wildcard
  // rules across all organism levels).
  const filteredRows = useMemo(() => {
    const orgQuery = filterOrganism.trim().toLowerCase();
    return (breakpoints || []).filter((bp) => {
      if (filterAntibiotic !== ALL && bp.whonetAbxCode !== filterAntibiotic)
        return false;
      if (filterPotency !== ALL && bp.potency !== filterPotency) return false;
      if (filterHost !== ALL && bp.host !== filterHost) return false;
      if (filterSite !== ALL) {
        const isBlank = !bp.siteOfInfection || !bp.siteOfInfection.trim();
        if (filterSite === "(General)") {
          if (!isBlank) return false;
        } else {
          if (bp.siteOfInfection !== filterSite) return false;
        }
      }
      if (filterGuideline !== ALL && composeGuideline(bp) !== filterGuideline)
        return false;
      if (orgQuery) {
        const code = (bp.organismCode || "").toLowerCase();
        const type = (bp.organismCodeType || "").toLowerCase();
        if (!code.includes(orgQuery) && !type.includes(orgQuery)) return false;
      }
      return true;
    });
  }, [
    breakpoints,
    filterAntibiotic,
    filterPotency,
    filterOrganism,
    filterHost,
    filterSite,
    filterGuideline,
  ]);

  // Reset page to 0 whenever the filtered set shrinks below the
  // current page position. (No deps array needed — this is a derived
  // value computed on each render.)
  const safePage = page > 0 && page * rowsPerPage >= filteredRows.length
    ? 0
    : page;

  const paginatedRows = useMemo(
    () =>
      filteredRows.slice(
        safePage * rowsPerPage,
        safePage * rowsPerPage + rowsPerPage,
      ),
    [filteredRows, safePage, rowsPerPage],
  );

  const handleClearFilters = () => {
    setFilterAntibiotic(ALL);
    setFilterPotency(ALL);
    setFilterOrganism("");
    setFilterHost(ALL);
    setFilterSite(ALL);
    setFilterGuideline(ALL);
    setPage(0);
  };

  // Helper to render a column-header dropdown filter.
  const renderFilterSelect = (label, value, setValue, options) => (
    <FormControl size="small" sx={{ minWidth: 140, mb: 0.5 }} fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(e) => {
          setValue(e.target.value);
          setPage(0);
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          Read-only browser of all AMRIE breakpoints. Use this to check
          what AMRIE already covers before defining a custom rule. Filters
          combine: each restricts the set further. Total:{" "}
          {(breakpoints || []).length} breakpoints; showing{" "}
          {filteredRows.length} after filters.
          {(filterAntibiotic !== ALL ||
            filterPotency !== ALL ||
            filterOrganism ||
            filterHost !== ALL ||
            filterSite !== ALL ||
            filterGuideline !== ALL) && (
            <>
              {" "}·{" "}
              <Box
                component="span"
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  color: "primary.main",
                }}
                onClick={handleClearFilters}
              >
                Clear filters
              </Box>
            </>
          )}
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ verticalAlign: "top", minWidth: 180 }}>
                <Box sx={{ fontWeight: "bold", mb: 0.5 }}>Antibiotic</Box>
                {renderFilterSelect(
                  "Code",
                  filterAntibiotic,
                  setFilterAntibiotic,
                  antibioticOptions,
                )}
              </TableCell>
              <TableCell sx={{ verticalAlign: "top", minWidth: 110 }}>
                <Box sx={{ fontWeight: "bold", mb: 0.5 }}>Potency</Box>
                {renderFilterSelect(
                  "Potency",
                  filterPotency,
                  setFilterPotency,
                  potencyOptions,
                )}
              </TableCell>
              <TableCell sx={{ verticalAlign: "top", minWidth: 160 }}>
                <Box sx={{ fontWeight: "bold", mb: 0.5 }}>Organism</Box>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="filter (code or level)"
                  value={filterOrganism}
                  onChange={(e) => {
                    setFilterOrganism(e.target.value);
                    setPage(0);
                  }}
                  sx={{ mb: 0.5 }}
                />
              </TableCell>
              <TableCell sx={{ verticalAlign: "top", minWidth: 130 }}>
                <Box sx={{ fontWeight: "bold", mb: 0.5 }}>Host</Box>
                {renderFilterSelect(
                  "Host",
                  filterHost,
                  setFilterHost,
                  hostOptions,
                )}
              </TableCell>
              <TableCell sx={{ verticalAlign: "top", minWidth: 180 }}>
                <Box sx={{ fontWeight: "bold", mb: 0.5 }}>
                  Clinical Indication
                </Box>
                {renderFilterSelect(
                  "Site",
                  filterSite,
                  setFilterSite,
                  siteOptions,
                )}
              </TableCell>
              <TableCell sx={{ verticalAlign: "top", minWidth: 120 }}>
                <Box sx={{ fontWeight: "bold", mb: 0.5 }}>R ≤ / S ≥</Box>
                <Box sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                  (no filter)
                </Box>
              </TableCell>
              <TableCell sx={{ verticalAlign: "top", minWidth: 140 }}>
                <Box sx={{ fontWeight: "bold", mb: 0.5 }}>Guideline</Box>
                {renderFilterSelect(
                  "Guideline",
                  filterGuideline,
                  setFilterGuideline,
                  guidelineOptions,
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">
                    No breakpoints match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((bp, i) => {
                const drug = antibioticByCode.get(bp.whonetAbxCode);
                const drugDisplay = drug?.antibiotic
                  ? `${drug.antibiotic} (${bp.whonetAbxCode})`
                  : bp.whonetAbxCode;
                const orgDisplay = `${bp.organismCode || "-"} (${
                  bp.organismCodeType || "-"
                })`;
                const rPart = bp.r != null ? `R≤${bp.r}` : null;
                const sPart = bp.s != null ? `S≥${bp.s}` : null;
                const breakpointPair =
                  rPart && sPart
                    ? `${rPart}, ${sPart}`
                    : rPart || sPart || "-";
                return (
                  <TableRow
                    key={bp.id || `${bp.whonetAbxCode}-${i}`}
                    hover
                  >
                    <TableCell>{drugDisplay}</TableCell>
                    <TableCell>{bp.potency || "-"}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {orgDisplay}
                    </TableCell>
                    <TableCell>{bp.host || "-"}</TableCell>
                    <TableCell>
                      {bp.siteOfInfection || (
                        <Box
                          component="span"
                          sx={{
                            color: "text.secondary",
                            fontStyle: "italic",
                          }}
                        >
                          (General)
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>
                      {breakpointPair}
                    </TableCell>
                    <TableCell>{composeGuideline(bp)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredRows.length}
        page={safePage}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Rows per page:"
      />
    </Box>
  );
}
